import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Loader2 } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const TYPED_GEOCODE_DEBOUNCE_MS = 900;
const PREDICTION_DEBOUNCE_MS = 280;
const MIN_TYPED_GEOCODE_LENGTH = 12;
const MIN_PREDICTION_LENGTH = 3;

const loaderPromises = new Map();

function loadGoogleMapsReady(apiKey) {
  if (!apiKey) return Promise.reject(new Error('Missing Google Maps API key'));
  if (!loaderPromises.has(apiKey)) {
    const loader = new Loader({
      apiKey,
      version: 'weekly',
      libraries: ['places'],
      id: '__soluqr_google_maps',
    });
    loaderPromises.set(
      apiKey,
      loader.load().then(async () => {
        if (window.google?.maps?.importLibrary) {
          await window.google.maps.importLibrary('maps');
          await window.google.maps.importLibrary('places');
        }
        const MapCtor = window.google?.maps?.Map;
        if (typeof MapCtor !== 'function') {
          throw new Error('google.maps.Map no está disponible tras cargar el script');
        }
      })
    );
  }
  return loaderPromises.get(apiKey);
}

function toLatLngLiteral(loc) {
  if (!loc) return null;
  const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
  const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
  if (typeof lat !== 'number' || typeof lng !== 'number' || Number.isNaN(lat) || Number.isNaN(lng)) {
    return null;
  }
  return { lat, lng };
}

function predictionLabel(prediction) {
  return (
    prediction?.description ||
    prediction?.structured_formatting?.main_text ||
    ''
  );
}

/**
 * Input de dirección + mapa.
 * Usa AutocompleteService (lista propia) en vez del widget Autocomplete de Google,
 * que reescribe el input al tipiar (p. ej. al poner espacio).
 */
const GoogleMapsAddressInput = ({
  address,
  setAddress,
  location,
  setLocation,
  placeholder = 'Ej: Av. Providencia 1234, Santiago',
  label = 'Dirección del Local',
  mapHint = 'Haz clic en el mapa o arrastra el marcador para afinar la ubicación exacta.',
  height = '300px',
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const initDoneRef = useRef(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const typedGeocodeTimerRef = useRef(null);
  const predictionTimerRef = useRef(null);
  const typedGeocodeQueryRef = useRef('');
  const applyPlaceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const userEditingRef = useRef(false);
  const lastGeocodedAddressRef = useRef('');
  const predictionReqIdRef = useRef(0);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [listOpen, setListOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const defaultLocation = { lat: -33.4489, lng: -70.6693 };
  const currentLocation = location || defaultLocation;

  const setAddressRef = useRef(setAddress);
  const setLocationRef = useRef(setLocation);
  setAddressRef.current = setAddress;
  setLocationRef.current = setLocation;

  const applyPlace = useCallback(({ location: loc, formattedAddress, viewport, updateAddress = true }) => {
    const m = mapInstanceRef.current;
    const mk = markerRef.current;
    if (!loc || !m || !mk) return;

    setLocationRef.current(loc);
    if (updateAddress && formattedAddress) {
      lastGeocodedAddressRef.current = formattedAddress;
      setAddressRef.current(formattedAddress);
    }

    if (!mk.getMap()) {
      mk.setMap(m);
    }

    if (viewport) {
      m.fitBounds(viewport);
    } else {
      m.setCenter(loc);
      m.setZoom(17);
    }
    m.panTo(loc);
    mk.setPosition(loc);
  }, []);

  applyPlaceRef.current = applyPlace;

  const scheduleTypedGeocode = useCallback((rawQuery) => {
    const query = String(rawQuery || '').trim();
    typedGeocodeQueryRef.current = query;

    if (typedGeocodeTimerRef.current) {
      clearTimeout(typedGeocodeTimerRef.current);
      typedGeocodeTimerRef.current = null;
    }

    // Solo mueve el pin; nunca reescribe el texto tipado.
    if (query.length < MIN_TYPED_GEOCODE_LENGTH) return;

    typedGeocodeTimerRef.current = setTimeout(() => {
      typedGeocodeTimerRef.current = null;
      if (typedGeocodeQueryRef.current !== query) return;
      if (!window.google?.maps?.Geocoder) return;

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: `${query}, Chile`, region: 'cl' }, (results, status) => {
        if (typedGeocodeQueryRef.current !== query) return;
        if (status !== 'OK' || !results?.[0]) return;
        const loc = toLatLngLiteral(results[0].geometry?.location);
        if (!loc) return;
        applyPlaceRef.current?.({
          location: loc,
          viewport: results[0].geometry?.viewport,
          updateAddress: false,
        });
      });
    }, TYPED_GEOCODE_DEBOUNCE_MS);
  }, []);

  const fetchPredictions = useCallback((rawQuery) => {
    const query = String(rawQuery || '').trim();
    if (predictionTimerRef.current) {
      clearTimeout(predictionTimerRef.current);
      predictionTimerRef.current = null;
    }
    if (query.length < MIN_PREDICTION_LENGTH || !autocompleteServiceRef.current) {
      setPredictions([]);
      setActiveIndex(-1);
      return;
    }

    const reqId = ++predictionReqIdRef.current;
    predictionTimerRef.current = setTimeout(() => {
      predictionTimerRef.current = null;
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: 'cl' },
          types: ['address'],
        },
        (results, status) => {
          if (reqId !== predictionReqIdRef.current) return;
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !results?.length) {
            setPredictions([]);
            setActiveIndex(-1);
            return;
          }
          setPredictions(results.slice(0, 6));
          setActiveIndex(-1);
          setListOpen(true);
        }
      );
    }, PREDICTION_DEBOUNCE_MS);
  }, []);

  const handleAddressChange = useCallback(
    (value) => {
      userEditingRef.current = true;
      setAddress(value);
      setLocation(null);
      setListOpen(true);
      fetchPredictions(value);
      scheduleTypedGeocode(value);
    },
    [setAddress, setLocation, fetchPredictions, scheduleTypedGeocode]
  );

  const selectPrediction = useCallback(
    (prediction) => {
      if (!prediction?.place_id) return;
      setListOpen(false);
      setPredictions([]);
      setActiveIndex(-1);

      const label = predictionLabel(prediction);
      if (label) {
        userEditingRef.current = false;
        setAddress(label);
      }

      const map = mapInstanceRef.current;
      if (!map || !window.google?.maps?.places?.PlacesService) return;

      if (!placesServiceRef.current) {
        placesServiceRef.current = new window.google.maps.places.PlacesService(map);
      }

      placesServiceRef.current.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['formatted_address', 'geometry', 'name'],
        },
        (details, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !details) return;
          const loc = toLatLngLiteral(details.geometry?.location);
          if (!loc) return;
          const formatted =
            details.formatted_address || details.name || label || inputRef.current?.value || '';
          typedGeocodeQueryRef.current = String(formatted).trim();
          lastGeocodedAddressRef.current = formatted;
          applyPlaceRef.current?.({
            location: loc,
            formattedAddress: formatted,
            viewport: details.geometry?.viewport,
            updateAddress: true,
          });
        }
      );
    },
    [setAddress]
  );

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) return;

    let cancelled = false;

    loadGoogleMapsReady(GOOGLE_MAPS_API_KEY)
      .then(() => {
        if (!cancelled) {
          setMapLoaded(true);
          setMapError(null);
        }
      })
      .catch((err) => {
        console.error('Google Maps load error:', err);
        if (!cancelled) {
          setMapError('No se pudo cargar Google Maps (revisa la API key, la red y la consola).');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const reverseGeocode = useCallback(
    async (coords) => {
      if (!window.google?.maps) return;
      const geocoder = new window.google.maps.Geocoder();
      try {
        const response = await geocoder.geocode({ location: coords });
        if (response.results?.[0]) {
          const formatted = response.results[0].formatted_address;
          lastGeocodedAddressRef.current = formatted;
          setAddress(formatted);
        }
      } catch (error) {
        console.error('Reverse geocoding error:', error);
      }
    },
    [setAddress]
  );

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || initDoneRef.current) return;

    const mapEl = mapRef.current;

    const run = async () => {
      try {
        if (typeof window.google?.maps?.Map !== 'function') {
          await loadGoogleMapsReady(GOOGLE_MAPS_API_KEY);
        }

        const center = currentLocation;
        const map = new window.google.maps.Map(mapEl, {
          center,
          zoom: location ? 16 : 12,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
        });
        mapInstanceRef.current = map;

        const marker = new window.google.maps.Marker({
          position: location ? center : null,
          map: location ? map : null,
          draggable: true,
          title: 'Arrastra para ajustar la ubicación',
        });
        markerRef.current = marker;

        marker.addListener('dragend', () => {
          const pos = marker.getPosition();
          if (!pos) return;
          const newLocation = { lat: pos.lat(), lng: pos.lng() };
          setLocation(newLocation);
          reverseGeocode(newLocation);
        });

        map.addListener('click', (e) => {
          const newLocation = { lat: e.latLng.lat(), lng: e.latLng.lng() };
          marker.setMap(map);
          marker.setPosition(newLocation);
          setLocation(newLocation);
          reverseGeocode(newLocation);
        });

        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        placesServiceRef.current = new window.google.maps.places.PlacesService(map);

        initDoneRef.current = true;
        setMapError(null);
      } catch (e) {
        console.error('GoogleMapsAddressInput init error:', e);
        setMapError('No se pudo inicializar el mapa. Revisa la consola y la configuración de la API key.');
      }
    };

    run();

    return () => {
      initDoneRef.current = false;
      if (typedGeocodeTimerRef.current) {
        clearTimeout(typedGeocodeTimerRef.current);
        typedGeocodeTimerRef.current = null;
      }
      if (predictionTimerRef.current) {
        clearTimeout(predictionTimerRef.current);
        predictionTimerRef.current = null;
      }
      mapInstanceRef.current = null;
      markerRef.current = null;
      placesServiceRef.current = null;
      autocompleteServiceRef.current = null;
      mapEl.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init una vez al cargar script
  }, [mapLoaded]);

  // Direcciones precargadas (padre) sin pin: solo ubicar mapa, no reescribir texto mientras tipia.
  useEffect(() => {
    if (!mapLoaded) return;
    const addr = String(address || '').trim();
    if (!addr || addr === lastGeocodedAddressRef.current) return;
    if (userEditingRef.current || document.activeElement === inputRef.current) return;
    if (location) return;

    let cancelled = false;
    const t = setTimeout(() => {
      if (userEditingRef.current || document.activeElement === inputRef.current) return;
      loadGoogleMapsReady(GOOGLE_MAPS_API_KEY)
        .then(() => {
          if (cancelled || !window.google?.maps?.Geocoder) return;
          if (userEditingRef.current) return;
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ address: `${addr}, Chile`, region: 'cl' }, (results, status) => {
            if (cancelled || userEditingRef.current) return;
            if (status !== 'OK' || !results?.[0]) return;
            const loc = toLatLngLiteral(results[0].geometry?.location);
            if (!loc) return;
            lastGeocodedAddressRef.current = addr;
            applyPlaceRef.current?.({
              location: loc,
              viewport: results[0].geometry?.viewport,
              updateAddress: false,
            });
          });
        })
        .catch((err) => console.error('Bootstrap geocode:', err));
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [mapLoaded, address, location]);

  useEffect(() => {
    const marker = markerRef.current;
    const map = mapInstanceRef.current;
    if (!marker || !map || !location) return;
    const loc = toLatLngLiteral(location) || location;
    if (!loc || typeof loc.lat !== 'number') return;
    marker.setMap(map);
    marker.setPosition(loc);
    map.setCenter(loc);
    if (map.getZoom() < 15) map.setZoom(16);
  }, [location]);

  useEffect(() => {
    const onDocPointer = (e) => {
      if (!listOpen) return;
      const t = e.target;
      if (inputRef.current?.contains(t) || listRef.current?.contains(t)) return;
      setListOpen(false);
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('touchstart', onDocPointer);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
    };
  }, [listOpen]);

  const onKeyDown = (e) => {
    if (!listOpen || !predictions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % predictions.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? predictions.length - 1 : i - 1));
      return;
    }
    if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      selectPrediction(predictions[activeIndex]);
      return;
    }
    if (e.key === 'Escape') {
      setListOpen(false);
      setActiveIndex(-1);
    }
  };

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <Input
          value={address}
          onChange={(e) => handleAddressChange(e.target.value)}
          placeholder={placeholder}
        />
        <p className="text-xs text-muted-foreground">
          Falta <code className="text-xs">REACT_APP_GOOGLE_MAPS_API_KEY</code> en{' '}
          <code className="text-xs">frontend/.env</code>
        </p>
      </div>
    );
  }

  const showSuggestions = listOpen && predictions.length > 0 && mapLoaded && !mapError;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="address-autocomplete">{label}</Label>
        <div className="relative" data-testid="address-autocomplete-input">
          <Input
            ref={inputRef}
            id="address-autocomplete"
            value={address ?? ''}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => handleAddressChange(e.target.value)}
            onFocus={() => {
              userEditingRef.current = true;
              if (predictions.length) setListOpen(true);
            }}
            onBlur={() => {
              window.setTimeout(() => {
                if (document.activeElement === inputRef.current) return;
                if (listRef.current?.contains(document.activeElement)) return;
                userEditingRef.current = false;
                setListOpen(false);
              }, 180);
            }}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            role="combobox"
            aria-expanded={showSuggestions}
            aria-autocomplete="list"
            aria-controls="address-suggestions"
          />
          {showSuggestions ? (
            <ul
              ref={listRef}
              id="address-suggestions"
              role="listbox"
              className="absolute left-0 right-0 top-full z-[10000] mt-1 max-h-60 overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md"
            >
              {predictions.map((p, idx) => {
                const main = p.structured_formatting?.main_text || p.description;
                const secondary = p.structured_formatting?.secondary_text || '';
                return (
                  <li key={p.place_id || `${main}-${idx}`} role="option" aria-selected={idx === activeIndex}>
                    <button
                      type="button"
                      className={`flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-accent ${
                        idx === activeIndex ? 'bg-accent' : ''
                      }`}
                      onMouseDown={(e) => {
                        // Evita blur del input antes del click.
                        e.preventDefault();
                        selectPrediction(p);
                      }}
                    >
                      <span className="font-medium text-foreground">{main}</span>
                      {secondary ? (
                        <span className="text-xs text-muted-foreground">{secondary}</span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
              <li className="border-t px-3 py-1.5 text-right text-[10px] text-muted-foreground">
                powered by Google
              </li>
            </ul>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">
          Escribe la dirección y elige una sugerencia cuando quieras. El texto no se rellena solo.
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Ubicación en el mapa</Label>
          {location && (
            <span className="text-xs text-green-600 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {Number(location.lat).toFixed(6)}, {Number(location.lng).toFixed(6)}
            </span>
          )}
        </div>

        {mapError && (
          <p className="text-sm text-destructive" role="alert">
            {mapError}
          </p>
        )}

        {mapLoaded ? (
          <div
            ref={mapRef}
            className="border rounded-lg overflow-hidden"
            style={{ height, width: '100%' }}
            data-testid="google-map-container"
          />
        ) : mapError ? (
          <div
            className="border rounded-lg flex items-center justify-center bg-muted px-4"
            style={{ height: '120px', width: '100%' }}
          >
            <p className="text-sm text-muted-foreground text-center">
              Podés seguir tipando la dirección arriba; el mapa no está disponible ahora.
            </p>
          </div>
        ) : (
          <div className="border rounded-lg flex items-center justify-center bg-muted" style={{ height }}>
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-sm text-muted-foreground mt-2">Cargando mapa...</p>
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground">{mapHint}</p>
      </div>
    </div>
  );
};

export default GoogleMapsAddressInput;
